import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectDealerSelected } from 'app/pages/Header/selectors';
import UnsoldApiService from '../apiService/unsoldApiService';
import { UnsoldMetric } from '../types';
import { metricTypeEnum } from 'app/pages/SalesPortalPage/LayoutV1/data/type';

export const useUnsoldPage = () => {
  const [metrics, setMetrics] = useState<UnsoldMetric[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showDataGrid, setShowDataGrid] = useState<boolean>(false);
  const [selectedMetric, setSelectedMetric] = useState<string | null>(null);
  const [selectedBox, setSelectedBox] = useState<number | null>(null);

  const selectedDealer = useSelector(selectDealerSelected);
  const selectedDealerId = selectedDealer?.dealer || '';
  
  // Store previous selected metric to preserve it on dealer change
  const previousSelectedMetricRef = useRef<string | null>(null);
  const previousSelectedBoxRef = useRef<number | null>(null);
  const previousShowDataGridRef = useRef<boolean>(false);
  const previousDealerIdRef = useRef<string>('');

  // Always keep refs updated with current selection state
  useEffect(() => {
    if (selectedMetric && showDataGrid) {
      previousSelectedMetricRef.current = selectedMetric;
      previousSelectedBoxRef.current = selectedBox;
      previousShowDataGridRef.current = showDataGrid;
    }
  }, [selectedMetric, showDataGrid, selectedBox]);

  // Fetch metrics data when dealer is selected or changes
  useEffect(() => {
    // Only fetch if dealer is selected
    if (!selectedDealerId) {
      setMetrics([]);
      setLoading(false);
      // Reset state when no dealer selected
      setShowDataGrid(false);
      setSelectedMetric(null);
      setSelectedBox(null);
      previousDealerIdRef.current = '';
      return;
    }

    const fetchMetrics = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Check if dealer actually changed (not just initial load)
        const dealerChanged = previousDealerIdRef.current && 
                             previousDealerIdRef.current !== selectedDealerId;
        
        const response = await UnsoldApiService.getMetrics(selectedDealerId);
        if (response.body?.metrics) {
          // Map UnsoldMetric to format expected by NurturingMetricsComponent
          const mappedMetrics = response.body.metrics.map((metric: UnsoldMetric) => ({
            ...metric,
            type: metric.type === 'NURTURING_PROSPECTS' 
              ? metricTypeEnum.opportunityAddedInNurturing 
              : metric.type,
          }));
          setMetrics(mappedMetrics as any);
          
          // Restore previous selection if dealer changed and selection existed
          if (dealerChanged && previousSelectedMetricRef.current && previousShowDataGridRef.current) {
            // Find the nurturing prospects metric (it's the only selectable one)
            const nurturingMetric = mappedMetrics.find(
              (m: any) => m.type === metricTypeEnum.opportunityAddedInNurturing ||
                         m.type === 'NURTURING_PROSPECTS' ||
                         m.metric_name?.toLowerCase().includes('nurturing')
            );
            
            if (nurturingMetric) {
              const metricIndex = mappedMetrics.findIndex(
                (m: any) => m.type === nurturingMetric.type || 
                           m.id === nurturingMetric.id
              );
              
              if (metricIndex !== -1) {
                // Restore the selection
                setSelectedMetric(metricTypeEnum.opportunityAddedInNurturing);
                setSelectedBox(metricIndex);
                setShowDataGrid(true);
              }
            }
          }
        }
        
        // Update previous dealer ID
        previousDealerIdRef.current = selectedDealerId;
      } catch (err) {
        console.error('Error fetching unsold metrics:', err);
        setError('Failed to load metrics. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, [selectedDealerId]);

  // Handle metric card click
  const handleMetricClick = (metric: any, index: number) => {
    try {
      // Only "Nurturing Prospects" card is clickable
      if (metric.type === metricTypeEnum.opportunityAddedInNurturing || 
          metric.type === 'NURTURING_PROSPECTS') {
        setSelectedMetric(metricTypeEnum.opportunityAddedInNurturing);
        setSelectedBox(index);
        setShowDataGrid(true);
      }
    } catch (error) {
      console.error('Error handling metric click:', error);
    }
  };

  return {
    metrics,
    loading,
    error,
    showDataGrid,
    selectedMetric,
    selectedBox: selectedBox || -1,
    selectedDealerId,
    handleMetricClick,
    setShowDataGrid,
    setSelectedBox,
  };
};

